import { type ReactElement, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType, useOutlet } from 'react-router';
import {
  resolveAuthTransitionDirection,
  type AuthTransitionDirection,
} from '../lib/authJourneyNavigation';

const TRANSITION_DURATION_MS = 220;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

type ActiveTransition = {
  direction: AuthTransitionDirection;
  outgoing: ReactElement | null;
};

const transitionClassNames: Record<
  AuthTransitionDirection,
  { incoming: string; outgoing: string }
> = {
  forward: {
    incoming: 'auth-route-transition-panel auth-route-transition-enter-forward',
    outgoing:
      'auth-route-transition-panel auth-route-transition-outgoing auth-route-transition-exit-forward',
  },
  backward: {
    incoming:
      'auth-route-transition-panel auth-route-transition-enter-backward',
    outgoing:
      'auth-route-transition-panel auth-route-transition-outgoing auth-route-transition-exit-backward',
  },
};

const readHistoryIndex = () => window.history.state?.idx as unknown;

export const AuthRouteTransition = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [transition, setTransition] = useState<ActiveTransition | null>(null);
  const incomingPanelRef = useRef<HTMLDivElement>(null);
  const previousOutletRef = useRef(outlet);
  const previousLocationKeyRef = useRef(location.key);
  const previousHistoryIndexRef = useRef<unknown>(readHistoryIndex());
  const cleanupTimerRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (previousLocationKeyRef.current === location.key) return;

    if (cleanupTimerRef.current !== null) {
      window.clearTimeout(cleanupTimerRef.current);
      cleanupTimerRef.current = null;
    }

    const outgoing = previousOutletRef.current;
    const currentHistoryIndex = readHistoryIndex();
    const direction = resolveAuthTransitionDirection(
      navigationType,
      location.state,
      previousHistoryIndexRef.current,
      currentHistoryIndex
    );

    previousOutletRef.current = outlet;
    previousLocationKeyRef.current = location.key;
    previousHistoryIndexRef.current = currentHistoryIndex;

    const focusIncomingHeading = () => {
      incomingPanelRef.current
        ?.querySelector<HTMLElement>('[data-auth-panel-heading]')
        ?.focus({ preventScroll: true });
    };

    if (direction === null || window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      setTransition(null);
      focusIncomingHeading();
      return;
    }

    setTransition({ direction, outgoing });
    cleanupTimerRef.current = window.setTimeout(() => {
      cleanupTimerRef.current = null;
      setTransition(null);
      focusIncomingHeading();
    }, TRANSITION_DURATION_MS);
  }, [location, navigationType, outlet]);

  useLayoutEffect(
    () => () => {
      if (cleanupTimerRef.current !== null) {
        window.clearTimeout(cleanupTimerRef.current);
      }
    },
    []
  );

  return (
    <div className="auth-route-transition-stage">
      {transition && (
        <div
          data-auth-route-panel="outgoing"
          className={transitionClassNames[transition.direction].outgoing}
          aria-hidden="true"
          inert
        >
          {transition.outgoing}
        </div>
      )}
      <div
        key={location.key}
        ref={incomingPanelRef}
        data-auth-route-panel={transition ? 'incoming' : 'current'}
        className={
          transition
            ? transitionClassNames[transition.direction].incoming
            : 'auth-route-transition-panel'
        }
      >
        {outlet}
      </div>
    </div>
  );
};
